// DumbbellPlotFilters.js
import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import Select from "react-select";
import { AsyncPaginate } from "react-select-async-paginate";
import DumbbellPlot from "./DumbbellPlot";
import {
  formateTimeRange,
  loadFromState,
  resetState,
  saveToState,
} from "../utils";
import { getProfiles, getNodes, getProfilerMetadata } from "../api/quontxApi";

const ITEMS_PER_PAGE = 25;

// Backend returns proxy profile under this uuid (shared across proxies) in the response metadata
const PROXY_PROFILE_UUID = "5666258c-3f67-4f16-8167-bff4a1cac760";

const DumbbellPlotFilters = ({
  timestampBuckets = [],
  domains = [],
  isLoading = false,
  propagnateData,
}) => {
  const maxDomainCount = 2;
  const maxEntityCount = 5;
  const propagnateFunctionRef = useRef(propagnateData);

  const [data, setData] = useState({});
  const [loadingData, setLoadingData] = useState(false);

  const [activeDomains, setActiveDomains] = useState([]);
  const [activeProxies, setActiveProxies] = useState([]);

  const [activeTimestampTo, setActiveTimestampTo] = useState(null);
  const [activeTimestampFrom, setActiveTimestampFrom] = useState(null);

  const [timestampFromOptions, setTimestampFromOptions] = useState([]);
  const [timestampToOptions, setTimestampToOptions] = useState([]);
  const [activeTimestampBuckets, setActiveTimestampBuckets] = useState([]);

  const [domainSelectDisabled, setDomainSelectDisabled] = useState(false);
  const [proxySelectDisabled, setProxySelectDisabled] = useState(false);

  const [proxyBuckets, setProxyBuckets] = useState([]);
  const [loadingBuckets, setLoadingBuckets] = useState(false);

  const manageActiveDomains = (selectedDomains) => {
    const safe = selectedDomains ?? [];
    if (safe.length <= maxDomainCount) {
      setDomainSelectDisabled(false);
      setActiveDomains(safe.map((d) => ({ label: d.label, value: d.value })));
      return;
    }
    setDomainSelectDisabled(true);
  };

  const manageActiveProxies = (selectedProxies) => {
    const safe = selectedProxies ?? [];
    if (safe.length <= maxEntityCount) {
      setActiveProxies(safe.map((p) => ({ label: p.label, value: p.value })));
    }
  };

  // ---------------------------
  // Server-side proxy search
  // ---------------------------

  const toOption = (node, disabled) => ({
    label: node.name,
    value: node.uuid,
    isDisabled: disabled,
  });

  const fetchProxiesPage = useCallback(async ({ search, page }) => {
    const res = await getNodes({
      node_type: "NODE_SET",
      name: search,
      page,
      itemsPerPage: ITEMS_PER_PAGE,
    });
    return res?.data?.data ?? [];
  }, []);

  const loadProxyOptions = useCallback(
    async (search, loadedOptions, additional) => {
      const q = (search || "").trim();
      if (q.length < 2) {
        return { options: [], hasMore: false, additional: { page: 1 } };
      }

      const page = additional?.page ?? 1;
      const items = await fetchProxiesPage({ search: q, page });

      return {
        options: items.map((n) => toOption(n, proxySelectDisabled)),
        hasMore: items.length === ITEMS_PER_PAGE,
        additional: { page: page + 1 },
      };
    },
    [fetchProxiesPage, proxySelectDisabled]
  );

  const proxyAdditional = useMemo(() => ({ page: 1 }), []);

  // ---------------------------
  // Timestamp buckets for selected proxies
  // (MUST use proxy UUIDs)
  // ---------------------------

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const proxyIds = (activeProxies ?? []).map((p) => p.value);

      if (!proxyIds.length) {
        setProxyBuckets([]);
        setActiveTimestampFrom(null);
        setActiveTimestampTo(null);
        return;
      }

      setLoadingBuckets(true);
      try {
        const allIntervals = [];

        for (const proxyId of proxyIds) {
          const res = await getProfilerMetadata({
            itemsPerPage: 1,
            entities: [proxyId], // ✅ proxy uuid, never PROXY_PROFILE_UUID
          });

          const rows = res?.data?.data ?? res?.data ?? [];
          rows.forEach((row) => {
            allIntervals.push(...(row?.metadata?.corpora?.time_intervals ?? []));
          });
        }

        const deduped = Array.from(new Set(allIntervals));
        deduped.sort();

        if (cancelled) return;

        setProxyBuckets(deduped);

        const fromVal = activeTimestampFrom?.value;
        const toVal = activeTimestampTo?.value;

        if (fromVal && !deduped.includes(fromVal)) setActiveTimestampFrom(null);
        if (toVal && !deduped.includes(toVal)) setActiveTimestampTo(null);
      } finally {
        if (!cancelled) setLoadingBuckets(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [activeProxies, activeTimestampFrom, activeTimestampTo]);

  const availableTimestampBuckets = useMemo(() => {
    const proxySelected = (activeProxies?.length ?? 0) > 0;
    return proxySelected ? (proxyBuckets ?? []) : (timestampBuckets ?? []);
  }, [activeProxies, proxyBuckets, timestampBuckets]);

  // ---------------------------
  // Timestamp dropdown options + idx computation
  // ---------------------------

  useEffect(() => {
    const fromIdx = availableTimestampBuckets.indexOf(activeTimestampFrom?.value);
    const toIdx = availableTimestampBuckets.indexOf(activeTimestampTo?.value);

    if (fromIdx < 0 || toIdx < 0) {
      setActiveTimestampBuckets([]);
      return;
    }

    setActiveTimestampBuckets([fromIdx, toIdx].sort((a, b) => a - b));
  }, [activeTimestampFrom, activeTimestampTo, availableTimestampBuckets]);

  useEffect(() => {
    const fromIdx = availableTimestampBuckets.indexOf(activeTimestampFrom?.value);

    setTimestampToOptions(
      availableTimestampBuckets.map((bucket, idx) => ({
        label: formateTimeRange(bucket),
        value: bucket,
        isDisabled: idx < 10 || (fromIdx >= 0 && idx < fromIdx),
      }))
    );
  }, [activeTimestampFrom, availableTimestampBuckets]);

  useEffect(() => {
    const toIdx = availableTimestampBuckets.indexOf(activeTimestampTo?.value);

    setTimestampFromOptions(
      availableTimestampBuckets.map((bucket, idx) => ({
        label: formateTimeRange(bucket),
        value: bucket,
        isDisabled:
          idx < 10 ||
          (toIdx >= 0 && idx > toIdx) ||
          availableTimestampBuckets.length - 1 === idx,
      }))
    );
  }, [activeTimestampTo, availableTimestampBuckets]);

  useEffect(() => {
    setTimestampFromOptions(
      availableTimestampBuckets.map((bucket, idx) => ({
        label: formateTimeRange(bucket),
        value: bucket,
        isDisabled: idx < 10 || availableTimestampBuckets.length - 1 === idx,
      }))
    );

    setTimestampToOptions(
      availableTimestampBuckets.map((bucket, idx) => ({
        label: formateTimeRange(bucket),
        value: bucket,
        isDisabled: idx < 10,
      }))
    );
  }, [availableTimestampBuckets]);

  const resetFilters = () => {
    resetState("dumbbellPlotState", [
      { stateFunc: setActiveDomains, nullVal: [] },
      { stateFunc: setActiveProxies, nullVal: [] },
      { stateFunc: setActiveTimestampBuckets, nullVal: [] },
      { stateFunc: setActiveTimestampFrom, nullVal: null },
      { stateFunc: setActiveTimestampTo, nullVal: null },
    ]);
    setDomainSelectDisabled(false);
    setProxySelectDisabled(false);
    setProxyBuckets([]);
    setData({});
  };

  // ---------------------------
  // Load profiles for selected proxies
  //
  // FIX:
  // - Request profiles with the SELECTED proxy UUIDs (not PROXY_PROFILE_UUID)
  // - Backend may still return profile metadata uuid = PROXY_PROFILE_UUID
  // - Map that shared profile to each proxy id in `data`
  // ---------------------------

  const loadProfiles = useCallback(async () => {
    const proxyIds = (activeProxies ?? []).map((p) => p.value);
    if (!proxyIds.length) {
      setData({});
      return;
    }

    // ✅ send the actual proxy uuids to the backend
    const profiles = await getProfiles({
      itemsPerPage: Math.max(proxyIds.length, 10),
      entities: proxyIds,
    });

    propagnateFunctionRef.current(profiles.data);

    const rows = profiles?.data?.data ?? [];

    // The backend might return:
    // - one row per proxy, or
    // - multiple rows but all with metadata.corpora.uuid == PROXY_PROFILE_UUID
    // We'll support both.
    const profileByReturnedUuid = {};
    const profileByRequestedUuid = {};

    for (const row of rows) {
      const returnedUuid = row?.metadata?.corpora?.uuid;
      if (returnedUuid) profileByReturnedUuid[returnedUuid] = row.values;

      // If backend echoes request id somewhere else, you can key by it here.
      // For now we rely on returnedUuid mapping below.
    }

    const sharedProxyProfile = profileByReturnedUuid[PROXY_PROFILE_UUID];

    const next = {};
    for (const proxyId of proxyIds) {
      // Prefer exact match if backend ever starts returning it
      if (profileByRequestedUuid[proxyId]) {
        next[proxyId] = profileByRequestedUuid[proxyId];
        continue;
      }

      // Otherwise use shared proxy profile (current backend behavior)
      if (sharedProxyProfile) {
        next[proxyId] = sharedProxyProfile;
      } else {
        console.warn(
          "[DumbbellPlotFilters] Missing shared proxy profile in response. expected uuid:",
          PROXY_PROFILE_UUID,
          "available:",
          Object.keys(profileByReturnedUuid)
        );
      }
    }

    setData(next);
  }, [activeProxies]);

  // ---------------------------
  // localStorage restore/persist
  // ---------------------------

  useEffect(() => {
    loadFromState("dumbbellPlotState", [
      {
        stateFunc: setActiveDomains,
        storageObjectKey: "activeDomains",
        defaultVal: [],
      },
      {
        stateFunc: setActiveProxies,
        storageObjectKey: "activeProxies",
        defaultVal: [],
      },
      {
        stateFunc: setActiveTimestampFrom,
        storageObjectKey: "activeTimestampFrom",
        defaultVal: null,
      },
      {
        stateFunc: setActiveTimestampTo,
        storageObjectKey: "activeTimestampTo",
        defaultVal: null,
      },
    ]);

    const propagnateDataFunctionCopy = propagnateFunctionRef.current;
    return () => {
      propagnateDataFunctionCopy({});
    };
  }, []);

  useEffect(() => {
    if (
      activeTimestampFrom !== null ||
      activeTimestampTo !== null ||
      activeDomains.length > 0 ||
      activeProxies.length > 0
    ) {
      saveToState("dumbbellPlotState", {
        activeTimestampFrom,
        activeTimestampTo,
        activeDomains,
        activeProxies,
      });
    }
  }, [activeTimestampFrom, activeTimestampTo, activeDomains, activeProxies]);

  // Fetch profiles when selection changes
  useEffect(() => {
    if ((activeProxies?.length ?? 0) === 0) return;

    (async () => {
      setLoadingData(true);
      try {
        await loadProfiles();
      } finally {
        setLoadingData(false);
      }
    })();
  }, [activeProxies, loadProfiles]);

  return (
    <div className="w-full flex flex-col items-center content-center">
      {isLoading && <p>Loading data...</p>}

      {!isLoading && domains && (
        <div className="flex flex-col gap-4 items-center justify-center mb-8">
          <div className="flex gap-4 justify-center mb-8">
            <div className="w-[240px] flex flex-col gap-2">
              <p>Organization Proxies</p>
              <AsyncPaginate
                isMulti
                value={activeProxies}
                onChange={(selected) => manageActiveProxies(selected)}
                loadOptions={loadProxyOptions}
                additional={proxyAdditional}
                debounceTimeout={250}
                cacheOptions
                placeholder="Type to search…"
                noOptionsMessage={({ inputValue }) =>
                  (inputValue || "").trim().length < 2
                    ? "Type at least 2 characters"
                    : "No matches"
                }
              />
            </div>

            <div className="w-[240px] flex flex-col gap-2">
              <p>Societal Domains</p>
              <Select
                isMulti
                isSearchable
                value={activeDomains}
                onChange={(selected) => manageActiveDomains(selected)}
                options={(domains ?? []).map((domain) => ({
                  label: domain.description,
                  value: domain.uuid,
                  isDisabled: domainSelectDisabled,
                }))}
              />
            </div>
          </div>

          <div className="flex gap-4 justify-center mb-8">
            <div className="w-[240px] flex flex-col gap-2">
              <p>Timestamp Bucket From</p>
              <Select
                isSearchable
                isDisabled={loadingBuckets || availableTimestampBuckets.length === 0}
                value={activeTimestampFrom}
                onChange={(selected) => setActiveTimestampFrom(selected)}
                options={timestampFromOptions}
                placeholder={loadingBuckets ? "Loading buckets…" : "Select…"}
              />
            </div>

            <div className="w-[240px] flex flex-col gap-2">
              <p>Timestamp Bucket To</p>
              <Select
                isSearchable
                isDisabled={loadingBuckets || availableTimestampBuckets.length === 0}
                value={activeTimestampTo}
                onChange={(selected) => setActiveTimestampTo(selected)}
                options={timestampToOptions}
                placeholder={loadingBuckets ? "Loading buckets…" : "Select…"}
              />
            </div>
          </div>

          <div>
            <button
              onClick={resetFilters}
              className="bg-blue-500 text-white font-semibold py-2 px-4 rounded hover:bg-blue-600 transition duration-200"
            >
              Reset filters
            </button>
          </div>

          {activeDomains?.length > 0 &&
            (activeProxies?.length ?? 0) > 0 &&
            Object.keys(data ?? {}).length > 0 &&
            (activeTimestampBuckets?.length ?? 0) === 2 && (
              <div className="flex items-center content-center flex-wrap">
                <DumbbellPlot
                  isLoading={loadingData}
                  data={data}
                  timebucketIdxs={activeTimestampBuckets}
                  activeDomains={activeDomains}
                  activeEntities={[]}
                  activeProxies={activeProxies}
                />
              </div>
            )}
        </div>
      )}
    </div>
  );
};

export default DumbbellPlotFilters;